import React, {useEffect, useRef} from 'react';
import {RefreshControl, ScrollView, View} from 'react-native';
import {AppState} from 'react-native';
import {useNavigation} from '@react-navigation/native';
import {createStackNavigator} from '@react-navigation/stack';
import {noop} from 'lodash';

import LoadingIndicator from '/component/base/LoadingIndicator';
import KeyboardAvoidingView from '/component/layout/KeyboardAvoidingView';
const ModalHeader = require('/component/layout/ModalHeader').default as any;
import LastChangeDateComponent from '/component/partial/LastChangeDateComponent';
import {createPopoverProvider} from '/provider/PopoverProvider';
import {black} from '/theme/color/on.color';
import {generateComponentCallback} from '/util/component.util';

import EditCandidateNotes from '../CandidateProfile/EditCandidateNotes';
import Notes from '../CandidateProfile/Notes';
import {Contact} from '../ReferralProfile/Contact';

import {Header} from './Header';
import {ReferralOfferExpiredBanner} from './ReferralOfferExpiredBanner';
import {useReferralProfile, useRefresh} from './ReferralProfile.hook';
import {styles} from './ReferralProfile.style';

interface ReferralProfileProps {
  recordId: string | null;
  onClose: () => void;
  hasProfile: boolean;
  fetchData: () => void;
  refreshCachedMetrics: () => void;
  resetCandidatesData: () => void;
  reportName: string;
  status: string;
  hasCandidateData: boolean;
  isDateExpired: boolean;
  candidateName?: string;
}

export const ReferralProfile: React.FC<ReferralProfileProps> = ({
  hasProfile = false,
  fetchData = noop,
  onClose = noop,
  recordId = null,
  refreshCachedMetrics = () => {},
  resetCandidatesData = noop,
  hasCandidateData = false,
  isDateExpired = false,
  candidateName = '',
}: any) => {
  const popoverProvider = createPopoverProvider();
  const {tt} = useReferralProfile({
    hasProfile,
    fetchData,
  });
  const navigation: any = useNavigation();
  const {Provider: PopoverProvider} = popoverProvider;
  const {refreshing, onRefreshRef} = useRefresh(async () => {
    refreshCachedMetrics();
  });
  const appState = useRef(AppState.currentState);

  useEffect(() => {
    const subscription = AppState.addEventListener('change', nextAppState => {
      if (
        appState.current.match(/inactive|background/) &&
        nextAppState === 'active'
      ) {
        resetCandidatesData();
      }
      appState.current = nextAppState;
    });

    return () => {
      subscription.remove();
    };
  }, [resetCandidatesData]);

  const renderLoading = () => {
    return (
      <ModalHeader
        isProfileScreen
        onClose={onClose}
        closeIconName={'chevron-left'}
        style={{base: styles.base}}>
        <View style={[styles.content, styles.contentLoading]}>
          <LoadingIndicator size="large" />
        </View>
      </ModalHeader>
    );
  };

  const screenComponent = ({popoverShow}: any) => {
    return (
      <ModalHeader
        isProfileScreen
        onClose={onClose}
        closeIconName={'chevron-left'}
        title={tt('referralProfile', 'Candidate profile')}
        style={{base: styles.base}}>
        {hasCandidateData && (
          <Header candidateId={recordId} isDateExpired={isDateExpired} />
        )}

        <ScrollView
          style={styles.content}
          contentContainerStyle={
            hasCandidateData
              ? [
                  styles.scrollViewContainer,
                  isDateExpired
                    ? {justifyContent: 'center'}
                    : {justifyContent: 'flex-start'},
                ]
              : null
          }
          refreshControl={
            <RefreshControl
              tintColor={black}
              refreshing={refreshing}
              onRefresh={onRefreshRef}
            />
          }>
          {hasCandidateData || !hasCandidateData ? (
            <>
              {isDateExpired && (
                <ReferralOfferExpiredBanner
                  popoverShow={popoverShow}
                  candidateId={recordId || ''}
                  userFullName={candidateName}
                  isExpired={isDateExpired}
                />
              )}

              <Contact
                candidateId={recordId}
                isArrow
                popoverShow={popoverShow}
                isCandidate={false}
              />

              {
                <Notes
                  showEditNotesLabel={hasCandidateData}
                  candidateId={recordId}
                  disabled={true}
                  greyOutTextInputValue={false}
                  onPress={() =>
                    navigation.navigate('referral-notes-edit-screen')
                  }
                  FooterComponent={LastChangeDateComponent}
                  enableEditNote={!isDateExpired}
                />
              }
            </>
          ) : undefined}

          <View style={styles.sections} />
        </ScrollView>
      </ModalHeader>
    );
  };

  const getMainScreenComponent = () => {
    const {Consumer: PopoverConsumer} = popoverProvider;

    return () => {
      return (
        <PopoverConsumer>
          {({popoverShow}: any) => {
            const ScreenComponent = screenComponent;
            return <ScreenComponent popoverShow={popoverShow} />;
          }}
        </PopoverConsumer>
      );
    };
  };

  const referralEditNotesComponent = () => {
    return () => {
      return (
        <EditCandidateNotes recordId={recordId} isReferralProfile={true} />
      );
    };
  };

  const getReferralProfileStackComponent = () => {
    const ProfileStackNavigator = createStackNavigator();
    const MainScreen = getMainScreenComponent();
    const EditNotesScreen = referralEditNotesComponent();

    return generateComponentCallback({
      component: (
        <ProfileStackNavigator.Navigator
          initialRouteName="referral-profile-main-screen"
          screenOptions={{headerShown: false}}>
          <ProfileStackNavigator.Screen
            name="referral-profile-main-screen"
            component={MainScreen}
          />
          <ProfileStackNavigator.Screen
            name="referral-notes-edit-screen"
            component={EditNotesScreen}
          />
        </ProfileStackNavigator.Navigator>
      ),
    });
  };

  const ProfileStack = getReferralProfileStackComponent();

  return (
    <PopoverProvider>
      {!hasProfile ? (
        renderLoading()
      ) : (
        <KeyboardAvoidingView style={styles.base}>
          <ProfileStack />
        </KeyboardAvoidingView>
      )}
    </PopoverProvider>
  );
};

export default ReferralProfile;
